import { VisibilityOff } from '@mui/icons-material'
import { Box, Stack } from '@mui/material'
import type { GridColDef, GridRowSelectionModel } from '@mui/x-data-grid'
import { useQueries } from '@tanstack/react-query'
import type { ReactNode } from 'react'
import { useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useSearchParams } from 'react-router-dom'

import CustomDataGrid from '@/components/dataDisplay/CustomDataGrid'
import CustomIconButton from '@/components/inputs/CustomIconButton'
import { useAlertContext } from '@/contexts/AlertContext'
import { ControllerListHeader } from '@/features/controller/components/bulkActions/ControllerListHeader'
import { ControllerListImporter } from '@/features/controller/components/bulkActions/ControllerListImporter'
import { SiteForController } from '@/features/controller/components/bulkActions/SiteForController'
import {
  GET_CONTROLLER_BY_EQUIPMENT_NUMBER_API,
  GET_CONTROLLER_DESCRIPTION_API,
  getControllerByEquipmentNumber,
  getControllerDescription,
} from '@/features/controller/endpoints/controllers'
import type { ControllerDescription, ControllerListIdentifier, SelectableController } from '@/features/controller/types'
import type { ControllerListIdentifiersParseResult } from '@/features/controller/utils/parseControllerListIdentifiers'
import { parseControllerListIdentifiers } from '@/features/controller/utils/parseControllerListIdentifiers'
import { retryOnNonUserError } from '@/features/controller/utils/retryOnNonUserError'

const ACTIONS_COLUMN_WIDTH = 58

interface ControllerListProps {
  bulkActionColumnTitle: string | null
  onControllerListUpdated: (controllers: SelectableController[]) => void
  renderBulkActionColumn: (controller: SelectableController) => ReactNode
  updateInProgress: boolean
}

export function ControllerList({
  bulkActionColumnTitle,
  onControllerListUpdated,
  renderBulkActionColumn,
  updateInProgress,
}: Readonly<ControllerListProps>) {
  const { t } = useTranslation()
  const { pushAlert } = useAlertContext()
  const [searchParams, setSearchParams] = useSearchParams()

  const [controllerListIdentifiers, setControllerListIdentifiers] = useState<ControllerListIdentifier[]>([])
  const [controllerListRowSelection, setControllerListRowSelection] = useState<GridRowSelectionModel>({
    ids: new Set(),
    type: 'include',
  })

  function updateControllerListIdentifiers({
    invalidIdentifiers,
    validIdentifiers,
  }: ControllerListIdentifiersParseResult) {
    if (invalidIdentifiers.length > 0) {
      pushAlert({
        message: t('controllers.bulk_actions.importer_invalid', { invalidIds: invalidIdentifiers.join(', ') }),
        severity: 'warning',
      })
    }
    setControllerListIdentifiers(validIdentifiers)
  }

  useEffect(() => {
    const urlIdList = searchParams.get('ids') || ''
    updateControllerListIdentifiers(parseControllerListIdentifiers(urlIdList))
  }, [searchParams])

  function handleControllerListIdentifiersUpdated(parseResult: ControllerListIdentifiersParseResult) {
    updateControllerListIdentifiers(parseResult)
    const urlIdList = parseResult.validIdentifiers.map((item) => item.rawValue).join(' ')
    setSearchParams(`ids=` + urlIdList)
  }

  // Fetch controller descriptions for all controllers in the list
  const controllerDescriptionQueries = useQueries({
    queries: controllerListIdentifiers.map(({ equipmentNumber, id }) => {
      if (id) {
        return {
          enabled: true,
          queryFn: () => getControllerDescription(id, { skipGlobalForbiddenErrorInterceptor: true }),
          queryKey: [GET_CONTROLLER_DESCRIPTION_API, id],
          retry: retryOnNonUserError,
        }
      } else {
        return {
          enabled: true,
          queryFn: () =>
            getControllerByEquipmentNumber(equipmentNumber!, { skipGlobalForbiddenErrorInterceptor: true }),
          queryKey: [GET_CONTROLLER_BY_EQUIPMENT_NUMBER_API, equipmentNumber],
          retry: retryOnNonUserError,
        }
      }
    }),
  })

  // Create a map of controller identifiers to their descriptions for an easy lookup
  const controllerIndexMap = useMemo(() => {
    const map = new Map<string, number>()
    controllerListIdentifiers.forEach((identifier, index) => {
      map.set(identifier.rawValue, index)
    })
    return map
  }, [controllerListIdentifiers])

  function getController(rawId: string): ControllerDescription | null {
    const index = controllerIndexMap.get(rawId)
    if (index === undefined) return null
    return controllerDescriptionQueries[index]?.data || null
  }

  let fetchedControllers = ''
  for (const identifier of controllerListIdentifiers) {
    if (getController(identifier.rawValue)) {
      fetchedControllers += '_' + identifier.rawValue
    }
  }

  useEffect(() => {
    handleControllerListUpdated(controllerListRowSelection)
  }, [fetchedControllers])

  function removeController(row: ControllerListIdentifier) {
    handleControllerListIdentifiersUpdated({
      validIdentifiers: controllerListIdentifiers.filter((id) => id !== row),
      invalidIdentifiers: [],
    })
  }

  function handleControllerListUpdated(selection: GridRowSelectionModel) {
    onControllerListUpdated(
      controllerListIdentifiers.flatMap((id) => {
        const controller = getController(id.rawValue)
        return controller
          ? [
              {
                controller,
                isSelected: selection.ids.has(id.rawValue),
              },
            ]
          : []
      }),
    )
  }

  function handleRowSelectionModelChange(newSelection: GridRowSelectionModel) {
    setControllerListRowSelection(newSelection)
    handleControllerListUpdated(newSelection)
  }

  const columns: GridColDef<ControllerListIdentifier>[] = [
    {
      field: 'equipmentNumber',
      flex: 1,
      headerName: t('controllers.controller'),
      renderCell: ({ row }) => {
        const index = controllerIndexMap.get(row.rawValue)
        if (index === undefined || !controllerDescriptionQueries[index]) {
          throw Error("Shouldn't get here")
        }
        return (
          <ControllerListHeader
            controllerListIdentifier={row}
            controllerQueryResult={controllerDescriptionQueries[index]}
          />
        )
      },
    },
    {
      field: 'site',
      flex: 2,
      headerName: 'Site',
      renderCell: ({ row }) => <SiteForController controller={getController(row.rawValue)} />,
      sortable: false,
    },
    ...(bulkActionColumnTitle
      ? [
          {
            field: 'bulkAction',
            flex: 5,
            headerName: bulkActionColumnTitle,
            renderCell: ({ row }) => {
              const controller = getController(row.rawValue)
              const isSelected = controllerListRowSelection.ids.has(row.rawValue)
              return controller ? renderBulkActionColumn({ controller, isSelected }) : <Box />
            },
            sortable: false,
          },
        ]
      : []),
    {
      field: 'actions',
      headerName: '',
      maxWidth: ACTIONS_COLUMN_WIDTH,
      minWidth: ACTIONS_COLUMN_WIDTH,
      renderCell: ({ row }) => (
        <Box>
          <CustomIconButton
            Icon={VisibilityOff}
            tooltipProps={{ title: t('controllers.bulk_actions.grid.remove') }}
            onClick={() => removeController(row)}
          />
        </Box>
      ),
      sortable: false,
    },
  ]

  return (
    <Stack gap={2}>
      <ControllerListImporter
        controllerList={controllerListIdentifiers}
        onControllerListUpdated={handleControllerListIdentifiersUpdated}
      />

      {controllerListIdentifiers.length > 0 && (
        <CustomDataGrid
          checkboxSelection={true}
          clickableRows={{
            isRowSelectable: ({ row }) => {
              return Boolean(getController(row.rawValue))
            },
            onRowSelectionModelChange: handleRowSelectionModelChange,
          }}
          columns={columns}
          disableRowSelectionExcludeModel={true}
          disableRowSelectionOnClick={true}
          getRowId={(row) => row.rawValue}
          includeWrapper={false}
          pageSize={20}
          rowSelection={{
            model: controllerListRowSelection,
            multiple: true,
          }}
          rows={controllerListIdentifiers}
          style={updateInProgress ? { border: '2px dashed lightblue' } : { border: '2px solid white' }}
        />
      )}
    </Stack>
  )
}
